import { useMemo } from 'react';
import userTableSetupService from '../../services/user_table_setup.service';

/*
 * Removes duplicate column names while preserving the first occurrence order.
 *
 * Example:
 * ['project_name', 'total_cost', 'project_name'] becomes
 * ['project_name', 'total_cost'].
 */
const uniqueColumns = columns => Array.from(new Set(columns));
const createColumnConfigByColumnName = tableConfig => {
  return tableConfig.columns.reduce((columnConfigByColumnName, columnName, index) => {
    return {
      ...columnConfigByColumnName,
      [columnName]: tableConfig.headers[index]
    };
  }, {});
};
const isColumnSelectable = _ref => {
  let {
    columnConfig,
    columnParams
  } = _ref;
  if (typeof columnConfig?.selectable !== 'function') return true;
  return !!columnConfig.selectable(columnParams);
};

/*
 * Builds the complete list of columns that must always stay selected.
 *
 * Example:
 * minimumRequiredColumns is ['project_name'] and the project_name header also
 * has required: true. The result is still ['project_name'], not a duplicate.
 */
const createRequiredColumns = _ref2 => {
  let {
    minimumRequiredColumns = [],
    tableConfig
  } = _ref2;
  const tableConfigRequiredColumns = tableConfig.columns.filter((column, index) => tableConfig.headers[index]?.required);
  return uniqueColumns([...minimumRequiredColumns, ...tableConfigRequiredColumns]);
};

/*
 * Creates a function that normalizes saved selected columns against the current
 * selector config.
 *
 * We persist only selectedColumns. Section names and checkbox groups are static
 * UI config from emptyColumnSelectorJson.sections, so they do not need to be
 * saved in user_table_setups.
 *
 * Example:
 * emptyColumnSelectorJson.sections contains project_name and total_cost.
 * saved selectedColumns is ['old_column', 'total_cost'].
 * requiredColumns is ['project_name'].
 *
 * Result:
 * { selectedColumns: ['total_cost', 'project_name'] }
 */
const createColumnSelectorJsonMerger = function (emptyColumnSelectorJson) {
  let requiredColumns = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const emptyColumnSelectorColumnNames = Object.values(emptyColumnSelectorJson.sections).flatMap(section => Object.keys(section));
  return columnSelectorJson => {
    const selectedColumns = uniqueColumns([
    // The user may have an old column selector json saved, that does not match the latest column definition we want to have.
    // This allows us to avoid manually migrating old jsons in the database. We just change the empty column selector json constant, and 
    // here we filter the existing selected columns by the ones that appear in that constant. So any old columns are removed automatically.
    // Next time the user saves the selection, they will be removed from the database as well.
    ...columnSelectorJson.selectedColumns.filter(column => emptyColumnSelectorColumnNames.includes(column)),
    // same for required columns
    ...requiredColumns.filter(column => emptyColumnSelectorColumnNames.includes(column))]);
    return {
      selectedColumns
    };
  };
};

/*
 * Converts the selected backend field names into the table config consumed by
 * the project table.
 *
 * The selector stores backend column names, while the table needs both:
 * - columns: backend field names used to read row values
 * - headers: header config objects at the same indexes as those columns
 *
 * Example:
 * selectedColumns: ['project_name', 'total_cost']
 * tableConfig.columns: ['project_name', 'project_code', 'total_cost']
 * tableConfig.headers: [{ text: 'name' }, { text: 'project_code' }, { text: 'total_cost' }]
 *
 * Result:
 * columns: ['project_name', 'total_cost']
 * headers: [{ text: 'name' }, { text: 'total_cost' }]
 * selectedBackendFields: ['project_name', 'total_cost']
 */
const createSelectedTableConfig = _ref3 => {
  let {
    columnConfigByColumnName,
    columnParams,
    columnSelectorJson,
    minimumRequiredColumns,
    tableConfig
  } = _ref3;
  const selectedColumnNames = columnSelectorJson.selectedColumns.length ? columnSelectorJson.selectedColumns : minimumRequiredColumns;
  const selectedBackendFields = selectedColumnNames.filter(column => tableConfig.columns.includes(column) && isColumnSelectable({
    columnConfig: columnConfigByColumnName[column],
    columnParams
  }));
  const headers = selectedBackendFields.map(column => {
    const headerIndex = tableConfig.columns.indexOf(column);
    return tableConfig.headers[headerIndex];
  });
  return {
    columnSelectorJson,
    columns: selectedBackendFields,
    headers,
    selectedBackendFields
  };
};

/*
 * Fetches, normalizes, and exposes the complete column-selector configuration
 * for a table.
 *
 * Responsibilities:
 * - fetch the user's saved table setup for tableName
 * - merge saved selectedColumns with the current table selector config
 * - force required columns to stay selected
 * - expose columnSelectorSections from the static config for checkbox rendering
 * - build the selected table headers/columns/backend fields
 * - expose saveTableSetup so the selector UI can persist changes
 *
 * Example:
 * A user previously saved ['total_cost'] for project_overview_table.
 * The current UK config marks project_name as required.
 *
 * Result:
 * columnSelectorJson.selectedColumns becomes ['total_cost', 'project_name'].
 * columnSelectorSections comes from emptyColumnSelectorJson.sections.
 * selectedTableConfig.columns becomes ['total_cost', 'project_name'].
 * requiredColumns becomes ['project_name'].
 */
export const useTableColumnSelectorConfig = _ref4 => {
  let {
    tableName,
    emptyColumnSelectorJson,
    minimumRequiredColumns = [],
    tableConfig,
    columnParams = {},
    setLoading,
    enabled = true
  } = _ref4;
  const {
    tableSetup: columnSelectorJson,
    setTableSetup: setColumnSelectorJson,
    tableSetupExists: columnSelectorSetupExists,
    setTableSetupExists: setColumnSelectorSetupExists
  } = userTableSetupService.useFetchTableSetup({
    tableName,
    defaultTableSetup: emptyColumnSelectorJson,
    fetchImmediately: enabled,
    setLoading
  });
  const columnConfigByColumnName = useMemo(() => {
    if (!enabled) return {};
    return createColumnConfigByColumnName(tableConfig);
  }, [enabled, tableConfig]);
  const requiredColumns = useMemo(() => {
    if (!enabled) return [];
    return createRequiredColumns({
      minimumRequiredColumns,
      tableConfig
    });
  }, [enabled, minimumRequiredColumns, tableConfig]);
  const mergeColumnSelectorJson = useMemo(() => {
    if (!enabled) return columnSelectorJson => columnSelectorJson;
    return createColumnSelectorJsonMerger(emptyColumnSelectorJson, requiredColumns);
  }, [emptyColumnSelectorJson, enabled, requiredColumns]);
  const mergedColumnSelectorJson = useMemo(() => {
    if (!enabled) return null;
    if (!columnSelectorJson) return null;
    return mergeColumnSelectorJson(columnSelectorJson);
  }, [columnSelectorJson, enabled, mergeColumnSelectorJson]);
  const selectedTableConfig = useMemo(() => {
    if (!enabled) return null;
    if (!mergedColumnSelectorJson) return null;
    return createSelectedTableConfig({
      columnConfigByColumnName,
      columnParams,
      columnSelectorJson: mergedColumnSelectorJson,
      minimumRequiredColumns,
      tableConfig
    });
  }, [columnConfigByColumnName, columnParams, enabled, mergedColumnSelectorJson, minimumRequiredColumns, tableConfig]);
  const saveTableSetup = userTableSetupService.useSaveTableSetup({
    tableName,
    tableSetupExists: columnSelectorSetupExists,
    setTableSetup: setColumnSelectorJson,
    setTableSetupExists: setColumnSelectorSetupExists,
    setLoading
  });
  return {
    columnConfigByColumnName,
    columnSelectorJson: mergedColumnSelectorJson,
    columnSelectorSections: emptyColumnSelectorJson.sections,
    mergeColumnSelectorJson,
    requiredColumns,
    saveTableSetup,
    selectedTableConfig
  };
};